import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { Check, Close, ExpandMore } from '@mui/icons-material';
import { FormikProps } from 'formik';
import Form from './form';
import { LastStepVM, DocumentVM } from 'civil-status/view-models/step-form';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { AxiosResponse } from 'axios';
import { HttpResponse } from 'shell/models/http-response';
import { DocumentWizard } from 'shell/models/document-wizard';
import { idStateDocumentWizard } from 'civil-status/constants/id-state-document-wizard';

interface DocumentProps {
  formContext: FormikProps<LastStepVM>;
  idEvento?: number;
  codLastStep?: string;
  disabled?: boolean;
  isLastStepEmergency?: boolean;
  isStepFromEmergency?: boolean;
}

const DocumentComponent = ({
  formContext,
  idEvento,
  codLastStep,
  disabled,
  isLastStepEmergency,
  isStepFromEmergency,
}: DocumentProps): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const [allegatiUsecaseData, setAllegatiUsecaseData] = useState<Array<DocumentWizard>>([]);

  const [allegatiUsecase, getAllegatiUsecase] = useHttpFetch<
    HttpResponse<DocumentWizard>,
    Array<DocumentWizard>
  >(`${__ACT_API_URL__}/allegati-use-case/list-allegati`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
    },
    transformer: (response: AxiosResponse<HttpResponse<DocumentWizard>>) => {
      const { data } = response;
      return data?.payload as Array<DocumentWizard>;
    },
  });

  useEffect(() => {
    if (codLastStep) {
      getAllegatiUsecase({
        data: {
          codUseCase: codLastStep || undefined,
        },
      });
    }
  }, [codLastStep]);

  const onChange = useEventCallback(
    (key: keyof DocumentVM, value: DocumentVM[keyof DocumentVM]) => {
      formContext.setFieldValue('document', {
        ...((formContext.values.document || {}) as DocumentVM),
        [key]: value,
      });
    }
  );

  const onChanges = useEventCallback((values: DocumentVM) => {
    formContext.setFieldValue('document', {
      ...((formContext.values.document || {}) as DocumentVM),
      ...values,
    });
  });

  const valuesData: DocumentVM | undefined = useMemo(() => {
    return formContext.values.document as DocumentVM | undefined;
  }, [formContext.values.document]);

  useEffect(() => {
    setAllegatiUsecaseData((oldState) => {
      if (oldState.length) {
        return oldState;
      }

      return (allegatiUsecase.data as Array<DocumentWizard>) || [];
    });
  }, [allegatiUsecase.data, valuesData]);

  const errorFiles = useMemo(() => {
    return (valuesData?.obligatoryFilesCurrent || []).length > 0;
  }, [valuesData?.obligatoryFilesCurrent]);

  const errorsDate = useMemo(() => {
    if (isLastStepEmergency) {
      return false;
    }

    return (
      (valuesData?.files?.filter((item) => item.codStato !== idStateDocumentWizard.Inserito) || [])
        ?.length > 0
    );
  }, [valuesData?.files, isLastStepEmergency]);

  return (
    <Fragment>
      {allegatiUsecaseData.length > 0 ? (
        <Accordion
          expanded={open}
          onChange={() => {
            setOpen(!open);
          }}
        >
          <AccordionSummary
            aria-controls="accordion-attachments-content"
            id="accordion-attachments-header"
            expandIcon={<ExpandMore />}
          >
            <Typography style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              Allegati &nbsp;
              {errorFiles || errorsDate ? <Close color="error" /> : <Check color="success" />}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Form
              onChange={onChange}
              onChanges={onChanges}
              values={valuesData}
              formContext={formContext}
              allegatiUsecaseData={allegatiUsecaseData}
              idEvento={idEvento}
              errorFiles={errorFiles}
              disabled={disabled}
              isStepFromEmergency={isStepFromEmergency}
            />
          </AccordionDetails>
        </Accordion>
      ) : undefined}
    </Fragment>
  );
};

DocumentComponent.displayName = 'Accordion Document Component';

const Document = memo(DocumentComponent);

export default Document;
